import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/api/client";
import ProductCard from "@/components/products/ProductCard";
import { SlidersHorizontal } from "lucide-react";
import SeoHead, { breadcrumbJsonLd } from "@/components/seo/SeoHead";

const Products = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState("popular");
  const [price, setPrice] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const url = new URLSearchParams();
    if (slug) url.set("category", slug);
    if (q) url.set("q", q);
    url.set("sort", sort);
    if (price === "low") url.set("max_price", "500");
    if (price === "mid") { url.set("min_price", "500"); url.set("max_price", "5000"); }
    if (price === "high") url.set("min_price", "5000");
    url.set("limit", "60");
    api.get(`/products?${url.toString()}`).then((r) => { setProducts(r.data); setLoading(false); });
  }, [slug, q, sort, price]);

  const currentCat = cats.find((c) => c.slug === slug);
  const title = currentCat?.name || (q ? `Search: "${q}"` : "All Products");

  return (
    <div className="container-fah py-8 sm:py-12" data-testid="page-products">
      <SeoHead
        title={currentCat ? `${currentCat.name} — Buy Online` : q ? `Search: ${q}` : "All Products"}
        description={currentCat
          ? `Buy ${currentCat.name.toLowerCase()} online from GST-verified sellers on FarmersAgroHub. ${currentCat.description || "Genuine products, best prices, pan-India delivery."}`
          : "Browse genuine seeds, pesticides, fertilizers, irrigation products and farm machinery from India's #1 multi-vendor agriculture marketplace."}
        path={slug ? `/category/${slug}` : "/products"}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", url: "/" },
          { name: "Categories", url: "/categories" },
          ...(currentCat ? [{ name: currentCat.name, url: `/category/${slug}` }] : []),
        ])}
      />
      <nav className="text-xs text-muted-foreground mb-3">
        <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/categories" className="hover:text-primary">Categories</Link>
        {currentCat && <> / <span className="text-foreground">{currentCat.name}</span></>}
      </nav>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="products-title">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{products.length} products available</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
          <select value={price} onChange={(e) => setPrice(e.target.value)} className="bg-white border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="filter-price">
            <option value="all">All Prices</option>
            <option value="low">Under ₹500</option>
            <option value="mid">₹500 - ₹5,000</option>
            <option value="high">Above ₹5,000</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="filter-sort">
            <option value="popular">Popularity</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-muted/60 rounded-xl animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">No products found. Try a different filter.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
};

export default Products;
